import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Show a search box only when the list is long enough that scanning it is slower
// than typing. Below this, no text input exists at all, so the mobile keyboard
// never opens.
const SEARCH_THRESHOLD = 8;

/**
 * Touch-friendly multi-select used instead of an Autocomplete: the field itself
 * is a plain (non-input) summary of the selected chips, and tapping it opens a
 * picker dialog — full-screen on phones — with a checkbox list and an explicit
 * Done button. Selection changes apply to `value`/`onChange` immediately;
 * closing the dialog never discards anything.
 *
 * `idKey`/`labelKey` name the option object's fields (e.g. "sportId"/"sportName").
 */
export default function MultiPickerField({ label, placeholder = "Select one or more", options, value, onChange, idKey, labelKey }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedIds = useMemo(() => new Set(value.map((v) => v[idKey])), [value, idKey]);

  const visibleOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o[labelKey] || "").toLowerCase().includes(q));
  }, [options, search, labelKey]);

  const openDialog = () => {
    setSearch("");
    setOpen(true);
  };

  const toggle = (option) => {
    if (selectedIds.has(option[idKey])) {
      onChange(value.filter((v) => v[idKey] !== option[idKey]));
    } else {
      onChange([...value, option]);
    }
  };

  const remove = (item) => onChange(value.filter((v) => v[idKey] !== item[idKey]));

  return (
    <>
      <Box
        onClick={openDialog}
        role="button"
        aria-label={label}
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 1,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          px: 1.5,
          py: 1.5,
          minHeight: "3.5rem",
          cursor: "pointer",
          "&:hover": { borderColor: "text.primary" }
        }}
      >
        <Typography
          variant="caption"
          sx={{ position: "absolute", top: -10, left: 10, px: 0.5, bgcolor: "background.paper", color: "text.secondary" }}
        >
          {label}
        </Typography>
        {value.length > 0 ? (
          <Box display="flex" gap={0.75} flexWrap="wrap" flex={1}>
            {value.map((item) => (
              <Chip
                key={item[idKey]}
                label={item[labelKey]}
                color="primary"
                variant="outlined"
                size="small"
                onDelete={(e) => {
                  e.stopPropagation();
                  remove(item);
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" flex={1}>
            {placeholder}
          </Typography>
        )}
        <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary", flexShrink: 0 }} />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullScreen={fullScreen} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          <Box>
            {label}
            {value.length > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {value.length} selected
              </Typography>
            )}
          </Box>
          <IconButton aria-label="Close" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {options.length > SEARCH_THRESHOLD && (
            <Box sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper", px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          )}
          <List disablePadding>
            {visibleOptions.map((option) => {
              const selected = selectedIds.has(option[idKey]);
              return (
                <ListItemButton key={option[idKey]} onClick={() => toggle(option)} sx={{ py: 1.25 }}>
                  <Checkbox checked={selected} disableRipple tabIndex={-1} sx={{ mr: 1, py: 0 }} />
                  <ListItemText primary={option[labelKey]} />
                </ListItemButton>
              );
            })}
            {visibleOptions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: "center" }}>
                No matches
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button disabled={value.length === 0} onClick={() => onChange([])} sx={{ mr: "auto" }}>
            Clear
          </Button>
          <Button variant="contained" disableElevation onClick={() => setOpen(false)} sx={{ minWidth: "6rem" }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
